import { computed, ref, watch } from 'vue'
import { projects } from '../data/projects'

/*
  Project detail is a real, linkable view — not a modal — but the site is still
  a single page with no router dependency. The hash carries the state:

      /#work/neo-pharmacy

  Back/forward, deep links and shareable URLs all work through the History API.
  If Vue Router is ever reintroduced, only this file needs to change.
*/

const HASH = /^#work\/(.+)$/

function fromHash(): string | null {
  if (typeof location === 'undefined') return null
  const match = location.hash.match(HASH)
  if (!match) return null
  const id = decodeURIComponent(match[1])
  return projects.some((p) => p.id === id) ? id : null
}

const activeId = ref<string | null>(fromHash())
let wired = false

function wire() {
  if (wired || typeof window === 'undefined') return
  wired = true

  window.addEventListener('popstate', () => {
    activeId.value = fromHash()
  })

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && activeId.value) close()
  })

  // Scroll behind the overlay would be disorienting, so lock the body while open.
  watch(
    activeId,
    (id) => {
      document.body.dataset.locked = id ? 'true' : 'false'
    },
    { immediate: true }
  )
}

function open(id: string) {
  if (activeId.value === id) return
  history.pushState({ work: id }, '', `#work/${encodeURIComponent(id)}`)
  activeId.value = id
}

function close() {
  // If we navigated here ourselves, step back so forward still works.
  if (history.state?.work) {
    history.back()
    return
  }
  history.replaceState({}, '', location.pathname + location.search)
  activeId.value = null
}

export function useWorkView() {
  wire()

  const activeProject = computed(
    () => projects.find((p) => p.id === activeId.value) ?? null
  )

  const activeIndex = computed(() =>
    projects.findIndex((p) => p.id === activeId.value)
  )

  const next = computed(() => {
    const i = activeIndex.value
    return i === -1 ? null : projects[(i + 1) % projects.length]
  })

  const previous = computed(() => {
    const i = activeIndex.value
    return i === -1 ? null : projects[(i - 1 + projects.length) % projects.length]
  })

  return { activeId, activeProject, activeIndex, next, previous, open, close }
}
